/**
 * Platform admin — SAM Session Log Viewer
 *
 * Two-panel layout:
 *  Left  — paginated, searchable list of agent sessions (summary row per session)
 *  Right — selected session: full entry timeline colour-coded by entry type
 *
 * Entry types rendered:
 *   outbound/trigger    🚀 context sent to OpenAI at session start
 *   outbound/other      📌 mid-session notification injected
 *   session_updated     🔄 agent page swap confirmed by OpenAI
 *   user                💬 user utterance (right-aligned)
 *   assistant           🤖 SAM response (left-aligned)
 *   tool_call           🔧 tool invoked
 *   tool_result         ✅/⚠  tool result
 */
import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  useListAgentSessionsQuery,
  useGetAgentSessionQuery,
  type AgentSessionSummary,
} from '@/redux/internal-services/platformAdminApi'
import SessionDebugger from './SessionDebugger'

// ── helpers ──────────────────────────────────────────────────────────────────

function fmtDate(iso: string) {
  try { return new Date(iso).toLocaleString() } catch { return iso }
}

function shortId(id?: string | null) {
  // tenant_id is null for platform / no-tenant sessions — guard so the list/detail don't crash.
  if (!id) return '—'
  return id.length > 12 ? id.slice(0, 8) + '…' + id.slice(-4) : id
}

// ── Session detail panel ──────────────────────────────────────────────────────

const SessionDetail: React.FC<{ sessionId: string }> = ({ sessionId }) => {
  const { t } = useTranslation()
  const { data, isLoading, isError } = useGetAgentSessionQuery(sessionId)
  const session = data?.data

  if (isLoading) return <div className="flex items-center justify-center h-full text-ui-text3 text-sm">Loading session…</div>
  if (isError || !session) return <div className="flex items-center justify-center h-full text-red-500 text-sm">{t('platform.sessionLoadFailed')}</div>

  return (
    <div className="h-full flex flex-col">
      {/* Header */}
      <div className="p-4 border-b border-ui-border2 flex-shrink-0">
        <p className="text-sm font-semibold text-ui-text">{session.user_name || shortId(session.user_id)}</p>
        <p className="text-xs font-mono text-ui-text3 mt-0.5">{session.session_id}</p>
        <div className="flex items-center gap-3 mt-1 text-xs text-ui-text3">
          <span>agent: <strong className="text-ui-text2">{session.agent_type}</strong></span>
          <span>tenant: <strong className="text-ui-text2">{shortId(session.tenant_id)}</strong></span>
          <span>{session.entries.length} events</span>
        </div>
      </div>

      {/* Timeline — interactive, filterable, searchable debugger */}
      <div className="flex-1 min-h-0">
        <SessionDebugger entries={session.entries} />
      </div>
    </div>
  )
}

// ── Page ─────────────────────────────────────────────────────────────────────

const SessionLogsPage: React.FC = () => {
  const [search, setSearch] = useState('')
  const [agentType, setAgentType] = useState('')
  const [page, setPage] = useState(1)
  const [selectedId, setSelectedId] = useState<string | null>(null)

  const { data, isLoading } = useListAgentSessionsQuery({
    search: search || undefined,
    agentType: agentType || undefined,
    page,
    limit: 50,
  })

  const sessions: AgentSessionSummary[] = data?.data ?? []
  const total = data?.total ?? 0

  return (
    <div className="flex h-full min-h-0">

      {/* ── Left: session list ── */}
      <div className="w-80 flex-shrink-0 border-r border-ui-border flex flex-col bg-ui-card">
        {/* Filters */}
        <div className="p-3 border-b border-ui-border2 space-y-2">
          <h2 className="text-sm font-semibold text-ui-text2">Session Logs</h2>
          <input
            value={search}
            onChange={e => { setSearch(e.target.value); setPage(1) }}
            placeholder="Search by user name, session ID…"
            className="w-full border border-ui-border rounded-lg px-2.5 py-1.5 text-xs focus:outline-none focus:ring-2 focus:ring-[#1c1849]/30 bg-ui-input text-ui-text"
          />
          <select
            value={agentType}
            onChange={e => { setAgentType(e.target.value); setPage(1) }}
            className="w-full border border-ui-border rounded-lg px-2.5 py-1.5 text-xs focus:outline-none focus:ring-2 focus:ring-[#1c1849]/30 bg-ui-input text-ui-text"
          >
            <option value="">All agent types</option>
            <option value="onboarding">onboarding</option>
          </select>
          <p className="text-xs text-ui-text3">{total} sessions</p>
        </div>

        {/* List */}
        <div className="flex-1 overflow-y-auto">
          {isLoading && <p className="text-xs text-ui-text3 p-4">Loading…</p>}
          {!isLoading && sessions.length === 0 && (
            <p className="text-xs text-ui-text3 p-4">No sessions found.</p>
          )}
          {sessions.map(s => (
            <button
              key={s.session_id}
              onClick={() => setSelectedId(s.session_id)}
              className={`w-full text-left px-3 py-2.5 border-b border-ui-border2 hover:bg-ui-hover transition ${selectedId === s.session_id ? 'bg-blue-50 dark:bg-brand-dark/10 border-l-2 border-l-[#1c1849] dark:border-l-brand-dark' : ''}`}
            >
              <p className="text-xs font-medium text-ui-text truncate">{s.user_name || shortId(s.user_id)}</p>
              <p className="text-[10px] font-mono text-ui-text3 truncate">{s.session_id.slice(0, 16)}…</p>
              <div className="flex items-center gap-2 mt-0.5">
                <span className="text-[10px] text-ui-text3">{fmtDate(s.started_at)}</span>
                <span className="text-[10px] text-ui-text3 ml-auto">{s.turn_count} turns · {s.entry_count} events</span>
              </div>
              <p className="text-[10px] text-ui-text3 truncate">{s.agent_type} · {shortId(s.tenant_id)}</p>
            </button>
          ))}
        </div>

        {/* Pagination */}
        {total > 50 && (
          <div className="p-2 border-t border-ui-border2 flex items-center justify-between text-xs text-ui-text2">
            <button disabled={page === 1} onClick={() => setPage(p => p - 1)} className="disabled:opacity-40 hover:text-[#1c1849]">← Prev</button>
            <span>Page {page}</span>
            <button disabled={sessions.length < 50} onClick={() => setPage(p => p + 1)} className="disabled:opacity-40 hover:text-[#1c1849]">Next →</button>
          </div>
        )}
      </div>

      {/* ── Right: detail ── */}
      <div className="flex-1 min-w-0 bg-ui-raised">
        {selectedId
          ? <SessionDetail sessionId={selectedId} />
          : (
            <div className="flex items-center justify-center h-full text-ui-text3 text-sm">
              Select a session to view its timeline.
            </div>
          )
        }
      </div>

    </div>
  )
}

export default SessionLogsPage
